import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { ProducePreviewOverlapLoadingOverlay } from "@ryde/controls";
import { useProducePollingContext, useRtl } from "@ryde/redux";
import {
  IInjectedDrawerProps,
  IRichViewLogsProps,
  Log,
  productionLogStateAtom,
  translateInfoItem,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer, FormFooter } from "../../drawer";
import { RichLogsFormWrapper } from "../rich-logs-components";
import {
  mergeLogDataWithProductionFilters,
  ProductionLogsContent,
  ProductionLogsHeader,
  ProductionViewLogsDrawerWrapperStyled,
} from "./parts";

export const ProductionViewLogsDrawer: FC<
  IRichViewLogsProps & IInjectedDrawerProps
> = ({
  drawerNumber,
  onCloseDrawer,
  logsFormWatched,
  logsFromWatchedApiOld,
  productionFilters,
  productionId,
}) => {
  const { t } = useTranslation();

  const logData = useRecoilValue(productionLogStateAtom);
  const { isRtl } = useRtl();
  const { fetchLogs } = useProducePollingContext();
  const [isLogDataLoading, setIsLogDataLoading] = useState(false);

  const loadLogs = useCallback(
    async ({
      overrideProductionId,
      isCancelled,
    }: {
      overrideProductionId?: number;
      isCancelled?: () => boolean;
    } = {}): Promise<void> => {
      setIsLogDataLoading(true);
      try {
        await fetchLogs(overrideProductionId);
      } finally {
        if (!isCancelled?.()) {
          setIsLogDataLoading(false);
        }
      }
    },
    [fetchLogs],
  );

  const handleRefreshLogsClick = (): void => {
    void loadLogs();
  };

  /* may be used for tests, so leaving this generator to for design polishing
  const logDataComputed: IRichLogData = useMemo(() => {
    const records = generateTestRichLogRecords(10);
    return { records };
  }, []);  */

  useEffect(() => {
    if (logsFromWatchedApiOld?.length) {
      Log.debug("Old api logs found, skip fetching new ones");
      return;
    }

    if (logsFormWatched?.records?.length && !productionId) {
      Log.debug("Inline form logs found, skip fetching new ones");
      return;
    }

    let cancelled = false;

    (async function load() {
      await loadLogs({
        overrideProductionId: productionId,
        isCancelled: () => cancelled,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loadLogs,
    logsFormWatched?.records?.length,
    logsFromWatchedApiOld?.length,
    productionId,
  ]);

  const logDataComputed = useMemo(
    () =>
      mergeLogDataWithProductionFilters(
        logsFormWatched ?? logData,
        productionFilters,
      ),
    [logData, logsFormWatched, productionFilters],
  );

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <RichLogsFormWrapper logData={logDataComputed}>
        <CommonDefaultDrawer
          onCloseDrawer={onCloseDrawer}
          Header={
            <ProductionLogsHeader
              onCloseDrawer={onCloseDrawer}
              logDataComputed={logDataComputed}
              logsFromWatchedApiOld={logsFromWatchedApiOld}
            />
          }
          Footer={
            <FormFooter
              cancelLabel={t("general.CLOSE")}
              onCancelClick={onCloseDrawer}
            />
          }
        >
          <ProductionViewLogsDrawerWrapperStyled $isRtl={isRtl}>
            {Boolean(logDataComputed) && (
              <ProductionLogsContent
                logData={logDataComputed}
                hasExternalLogs={Boolean(
                  logsFormWatched || logsFromWatchedApiOld,
                )}
                onRefreshLogsClick={handleRefreshLogsClick}
                isRefreshLogsDisabled={isLogDataLoading}
              />
            )}
            {Boolean(logsFromWatchedApiOld) &&
              logsFromWatchedApiOld?.map((log, index) => (
                <div key={index} className="log-item__old">
                  <span className="logText">{translateInfoItem(log, t)}</span>
                </div>
              ))}
            {isLogDataLoading && (
              <ProducePreviewOverlapLoadingOverlay
                caption={`${t("ui.LOADING_DATA")}...`}
              />
            )}
          </ProductionViewLogsDrawerWrapperStyled>
        </CommonDefaultDrawer>
      </RichLogsFormWrapper>
    </BaseDrawer>
  );
};
